import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import AdminLayout from "../components/AdminLayout";
import "../components/settings/settingsCompact.css";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";
import { 
  Building2, CreditCard, Users, FileText, Package, 
  Plug, Bell, ChevronRight, Plus, Trash2, Edit,
  Upload, ExternalLink, Check, X, Mail, Shield,
  Loader2, Link2, Eye, EyeOff, AlertCircle, Lock,
  Download, User, UserCheck, FileSpreadsheet, History, CheckCircle, Shirt, Clock,
  DollarSign, RefreshCw, UserPlus, Calendar, Crown, Receipt, Crop, Heart, Unlink,
  Search, Code2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

// Initialize Stripe for subscription payments
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
import ImportWizard from "../components/ImportWizard";
import OrgMerchandise from "../components/OrgMerchandise";
import ComplianceSettings from "../components/ComplianceSettings";
import SquadlineIntegration from "../components/SquadlineIntegration";
import ImageCropper from "../components/ImageCropper";
import SetupOverviewTab from "../components/settings/SetupOverviewTab";
import useAdminAccessMutations from "../components/settings/useAdminAccessMutations";

// Import refactored tab components
import {
  AdministratorsTab,
  BankingTab,
  BillingTab,
  DetailsTab,
  DocumentsTab,
  FeesTab,
  FoundationTab,
  ImportTab,
  IntegrationsTab,
  NotificationsTab,
  ProductsTab,
  SubscriptionTab
} from "../components/settings";

const TABS = [
  { id: "setup", label: "Overview", icon: CheckCircle, group: "Overview" },
  { id: "details", label: "Profile & Branding", icon: Building2, group: "Organization" },
  { id: "subscription", label: "Pricing Plan", icon: RefreshCw, group: "Money" },
  { id: "banking", label: "Payouts & Processing", icon: CreditCard, group: "Money" },
  { id: "billing", label: "Sporthouse Billing", icon: Receipt, group: "Money" },
  { id: "administrators", label: "Administrators", icon: Users, group: "People & Safety" },
  { id: "compliance", label: "Compliance", icon: Shield, group: "People & Safety" },
  { id: "notifications", label: "Notifications", icon: Bell, group: "People & Safety" },
  { id: "documents", label: "Document Library", icon: FileText, group: "Content & Data" },
  { id: "products", label: "Products", icon: Package, group: "Content & Data" },
  { id: "merchandise", label: "Merchandise", icon: Shirt, group: "Content & Data" },
  { id: "import", label: "Import Data", icon: Upload, group: "Content & Data" },
  { id: "integrations", label: "Integrations", icon: Plug, group: "Connections" },
  { id: "developer", label: "Developer", icon: Code2, group: "Connections" },
  { id: "foundation", label: "Foundation", icon: Heart, group: "Platform" },
];

const ADMIN_ACCESS_LEVELS = [
  { id: "full", label: "Full administrator", description: "All organization settings, money, people, and programs" },
  { id: "operations", label: "Operations manager", description: "Programs, registrations, teams, check-in, and reports" },
  { id: "finance", label: "Finance manager", description: "Payments and financial reports" },
  { id: "view_only", label: "View only", description: "Read-only reporting access" },
];

const EmbeddedLayout = ({ children }) => <>{children}</>;

const SettingsHeader = () => (
  <header className="settings-content-header">
    <h1 id="organization-settings-heading" className="settings-content-title">Organization Settings</h1>
    <p>Manage your organization profile, payments, people, integrations, and platform preferences.</p>
  </header>
);

const AdminOrganizationSettings = ({ embedded = false, developerPath = "/admin/developer" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { user, token } = useAuth();
  const accessMutations = useAdminAccessMutations(user?.org_id, user?.user_id, token);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  
  // Handle URL query param for tab
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam && TABS.some(t => t.id === tabParam)) {
      return tabParam;
    }
    return "setup";
  });
  const [settingsSearch, setSettingsSearch] = useState("");
  const sectionTypeahead = useRef({ text: "", at: 0 });
  const handleSectionTypeahead = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      event.preventDefault();
      return;
    }
    if (event.ctrlKey || event.altKey || event.metaKey || event.key.length !== 1) return;
    const now = Date.now();
    const previous = sectionTypeahead.current;
    // A bare Space still selects; a Space in an active search is text.
    if (event.key === " " && (!previous.text || now - previous.at >= 1000)) return;
    // Capture before the item's Space selection handler. Keep focus synchronous
    // so an immediate Enter/Escape cannot leave a deferred unmounted-option focus.
    event.preventDefault();
    const text = (now - previous.at < 1000 ? previous.text : "") + event.key.toLowerCase();
    sectionTypeahead.current = { text, at: now };
    const search = [...text].every((character) => character === text[0]) ? text[0] : text;
    const options = [...event.currentTarget.querySelectorAll('[role="option"]')]
      .filter((option) => !option.hasAttribute("data-disabled"));
    const current = options.indexOf(document.activeElement);
    const ordered = [...options.slice(current + 1), ...options.slice(0, current + 1)];
    const match = (search.length > 1 && options[current]?.textContent.toLowerCase().startsWith(search))
      ? options[current]
      : ordered.find((option) => option.textContent.toLowerCase().startsWith(search));
    match?.focus();
  };
  
  // Organization Details
  const [orgDetails, setOrgDetails] = useState({
    name: "",
    description: "",
    sport: "",
    logo_url: "",
    splash_image_url: "",
    sidebar_image_url: "",
    website: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    primary_color: "#000000",
    secondary_color: "#ffffff",
    subdomain: "",
    tax_id: "",
    show_address: true,
    show_tax_id: false
  });
  
  // Subdomain
  const [subdomainChecking, setSubdomainChecking] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [subdomainError, setSubdomainError] = useState(null);

  // Banking / Stripe Connect
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [disconnectingStripe, setDisconnectingStripe] = useState(false);

  // Administrators
  const [admins, setAdmins] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const documentOpenerRef = useRef(null);
  const productOpenerRef = useRef(null);
  const productImageInputRef = useRef(null);
  const openDocumentDialog = (open) => {
    if (open) documentOpenerRef.current = document.activeElement;
    setDocDialogOpen(open);
  };
  const openProductDialog = (open) => {
    if (open) productOpenerRef.current = document.activeElement;
    setProductDialogOpen(open);
  };
  const restoreOperationalOpener = (event, ref) => {
    event.preventDefault();
    const opener = ref.current;
    if (opener?.isConnected && !opener.disabled && !opener.closest('[hidden], [inert]')) {
      opener.focus();
    } else {
      settingsContentRef.current?.focus();
    }
  };
  const inviteOpenerRef = useRef(null);
  const settingsContentRef = useRef(null);
  const openInviteDialog = (open) => {
    if (open) {
      const opener = settingsContentRef.current?.querySelector('[data-testid="invite-admin-btn"]');
      inviteOpenerRef.current = opener || document.activeElement;
    }
    setInviteDialogOpen(open);
  };
  const [inviteData, setInviteData] = useState({ email: "", access_level: "operations" });
  const [inviting, setInviting] = useState(false);

  // Document Library
  const [documents, setDocuments] = useState([]);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docData, setDocData] = useState({ name: "", type: "waiver", content: "" });

  // Products
  const [products, setProducts] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [operationalDialogElement, setOperationalDialogElement] = useState(null);
  useEffect(() => {
    if (!docDialogOpen && !productDialogOpen) return;
    const dialog = operationalDialogElement;
    if (!dialog) return;
    let panel;
    let observer;
    const measure = () => {
      const bounds = panel?.getBoundingClientRect();
      const rect = dialog.getBoundingClientRect();
      const style = panel && getComputedStyle(panel);
      const overlaps = bounds && bounds.left < rect.right && bounds.right > rect.left;
      const visible = style?.position === 'fixed' && style.display !== 'none' &&
        style.visibility !== 'hidden' && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight;
      if (visible && overlaps) {
        dialog.style.top = '12px';
        dialog.style.transform = 'translateX(-50%)';
        dialog.style.maxHeight = `${Math.max(0, bounds.top - 24)}px`;
        dialog.style.overflowY = 'auto';
      } else {
        ['top', 'transform', 'max-height', 'overflow-y'].forEach(property => dialog.style.removeProperty(property));
      }
    };
    const connect = () => {
      const next = document.getElementById('sporthouse-analytics-consent');
      if (next !== panel) {
        observer?.disconnect();
        panel = next;
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(measure);
          observer.observe(dialog);
          if (panel) observer.observe(panel);
        }
      }
      measure();
    };
    connect();
    const mutations = new MutationObserver(connect);
    mutations.observe(document.body, { childList: true });
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      mutations.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [docDialogOpen, productDialogOpen, operationalDialogElement]);

  const [productData, setProductData] = useState({
    name: "", description: "", price: "", photo_url: "",
    has_sizes: false, sizes: [], has_colors: false, colors: []
  });

  // Notifications
  const [notificationSettings, setNotificationSettings] = useState({
    email_new_registration: true,
    email_payment_received: true,
    email_waitlist_spot: true,
    email_daily_summary: false,
    email_weekly_report: true
  });

  // Import Data
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [selectedImportType, setSelectedImportType] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingImportHistory, setLoadingImportHistory] = useState(false);

  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedUpgradeTier, setSelectedUpgradeTier] = useState(null);
  const [selectedUpgradeInterval, setSelectedUpgradeInterval] = useState('monthly');
  const [upgradeClientSecret, setUpgradeClientSecret] = useState(null);
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState(null);

  // Image cropper state for logo
  const [logoCropperOpen, setLogoCropperOpen] = useState(false);
  const [logoCropperImage, setLogoCropperImage] = useState(null);

  const organizationRequest = useRef(0);
  useEffect(() => {
    setOrganization(null);
    setLoading(true);
    fetchOrganization();
    return () => { organizationRequest.current += 1; };
  }, [token, user?.org_id]);

  useEffect(() => {
    const handleHistoryChange = () => {
      const tab = new URLSearchParams(window.location.search).get("tab") || "setup";
      if (TABS.some((item) => item.id === tab)) setActiveTab(tab);
    };
    window.addEventListener("popstate", handleHistoryChange);
    return () => window.removeEventListener("popstate", handleHistoryChange);
  }, []);

  const navigateToTab = (tabId) => {
    setActiveTab(tabId);
    const nextUrl = new URL(window.location.href);
    if (tabId === "setup") nextUrl.searchParams.delete("tab");
    else nextUrl.searchParams.set("tab", tabId);
    window.history.pushState({}, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  };

  // Fetch import history when import tab is selected
  useEffect(() => {
    const fetchImportHistory = async () => {
      if (activeTab !== "import" || !token) return;
      
      setLoadingImportHistory(true);
      try {
        const response = await axios.get(`${API}/import/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setImportHistory(response.data || []);
      } catch (error) {
        console.error("Error fetching import history:", error);
      } finally {
        setLoadingImportHistory(false);
      }
    };
    
    fetchImportHistory();
  }, [activeTab, token]);

  // Fetch subscription data when subscription tab is selected
  useEffect(() => {
    const fetchSubscription = async () => {
      if (activeTab !== "subscription" || !token) return;
      
      setSubscriptionLoading(true);
      try {
        const response = await axios.get(`${API}/subscriptions/org/current`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionData(response.data);
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    
    fetchSubscription();
  }, [activeTab, token]);

  // Check URL for subscription return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    if (subscriptionStatus === 'success') {
      setActiveTab('subscription');
      toast.success("Subscription updated successfully!");
      window.history.replaceState({}, '', window.location.pathname);
    } else if (subscriptionStatus === 'cancelled') {
      setActiveTab('subscription');
      toast.info("Subscription checkout was cancelled");
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // One read lifecycle owns live status; persisted flags are not current evidence.
  const [stripeStatus, setStripeStatus] = useState({ loading: true, error: false });
  const [stripeStatusRetry, setStripeStatusRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setStripeConnected(false);
    setStripeAccount(null);
    setStripeStatus({ loading: true, error: false });
    if (!organization || organization.org_id !== user?.org_id || !token) return () => { current = false; };
    const accountId = organization.stripe_account_id;
    if (!accountId && activeTab !== "banking" && !new URLSearchParams(window.location.search).has('stripe_connect')) {
      setStripeStatus({ loading: false, error: false });
      return () => { current = false; };
    }
    setStripeConnected(!!accountId);
    setStripeAccount(accountId ? { id: accountId } : null);
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/organizations/${user.org_id}/stripe-connect/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!current) return;
        if (data.cached || typeof data.connected !== "boolean" ||
            (data.connected && [data.charges_enabled, data.payouts_enabled, data.details_submitted].some(value => typeof value !== "boolean"))) {
          setStripeStatus({ loading: false, error: true });
          return;
        }
        setStripeConnected(data.connected);
        setStripeAccount(data.connected ? {
          id: data.account_id, status: data.status,
          charges_enabled: data.charges_enabled, payouts_enabled: data.payouts_enabled,
          details_submitted: data.details_submitted
        } : null);
        setStripeStatus({ loading: false, error: false });
        if (new URLSearchParams(window.location.search).has('stripe_connect')) {
          if (data.charges_enabled) toast.success("Stripe Connect setup complete!");
          else if (data.details_submitted) toast.info("Stripe account pending verification");
        }
      } catch {
        if (current) setStripeStatus({ loading: false, error: true });
      }
    };
    check();
    return () => { current = false; };
  }, [organization?.org_id, organization?.stripe_account_id, user?.org_id, token, activeTab, stripeStatusRetry]);

  const fetchOrganization = async () => {
    const request = ++organizationRequest.current;
    if (!user?.org_id) {
      setLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch organization data without images first (faster load)
      const response = await axios.get(`${API}/organizations/${user.org_id}`, { headers });
      if (request !== organizationRequest.current) return;
      const org = response.data;
      setOrganization(org);
      
      // Initialize details without images
      setOrgDetails({
        name: org.name || "",
        description: org.description || "",
        sport: org.sport || "",
        logo_url: "", // Will be loaded separately
        splash_image_url: "", // Will be loaded separately
        sidebar_image_url: "", // Will be loaded separately
        website: org.website || "",
        contact_email: org.contact_email || "",
        contact_phone: org.contact_phone || "",
        address: org.address || "",
        city: org.city || "",
        state: org.state || "",
        zip_code: org.zip_code || "",
        primary_color: org.primary_color || "#000000",
        secondary_color: org.secondary_color || "#ffffff",
        subdomain: org.subdomain || "",
        tax_id: org.tax_id || "",
        show_address: org.show_address !== false,
        show_tax_id: org.show_tax_id || false
      });
      
      // Fetch images separately (lazy loading for better performance)
      if (org.has_logo || org.has_splash_image || org.has_sidebar_image || activeTab === "details") {
        try {
          const imgResponse = await axios.get(`${API}/organizations/${user.org_id}/images`, { headers });
          if (request !== organizationRequest.current) return;
          const images = imgResponse.data;
          setOrgDetails(prev => ({
            ...prev,
            logo_url: images.logo_url || "",
            splash_image_url: images.splash_image_url || "",
            sidebar_image_url: images.sidebar_image_url || ""
          }));
        } catch (imgError) {
          console.error("Error fetching organization images:", imgError);
        }
      }
      
      if (request !== organizationRequest.current) return;
      // Reset subdomain availability if it's already set
      if (org.subdomain) {
        setSubdomainAvailable(true);
      }

      // Load admins
      if (org.administrators) {
        setAdmins(org.administrators);
      }

      // Load documents
      if (org.document_library) {
        setDocuments(org.document_library);
      }

      // Load products
      if (org.products) {
        setProducts(org.products);
      }

      // Load notification settings
      if (org.notification_settings) {
        setNotificationSettings(org.notification_settings);
      }

    } catch (error) {
      if (request !== organizationRequest.current) return;
      console.error("Error fetching organization:", error);
      toast.error("Failed to load organization settings");
    } finally {
      if (request === organizationRequest.current) setLoading(false);
    }
  };

  // Check subdomain availability with debounce
  const checkSubdomainAvailability = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      setSubdomainError(null);
      return;
    }
    
    // Skip check if it's the current subdomain
    if (subdomain === organization?.subdomain) {
      setSubdomainAvailable(true);
      setSubdomainError(null);
      return;
    }
    
    setSubdomainChecking(true);
    setSubdomainError(null);
    
    try {
      const response = await axios.get(`${API}/organizations/check-subdomain/${subdomain}`);
      setSubdomainAvailable(response.data.available);
      if (!response.data.available) {
        setSubdomainError(response.data.reason);
      }
    } catch (error) {
      setSubdomainAvailable(false);
      setSubdomainError("Failed to check availability");
    } finally {
      setSubdomainChecking(false);
    }
  };
  
  // Debounced subdomain check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (orgDetails.subdomain) {
        checkSubdomainAvailability(orgDetails.subdomain);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [orgDetails.subdomain]);

  const handleSaveDetails = async () => {
    // Validate subdomain if changed
    if (orgDetails.subdomain && orgDetails.subdomain !== organization?.subdomain && !subdomainAvailable) {
      toast.error("Please choose an available subdomain");
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // If subdomain changed, create it in Cloudflare first
      if (orgDetails.subdomain && orgDetails.subdomain !== organization?.subdomain) {
        try {
          await axios.post(
            `${API}/cloudflare/subdomain/create`,
            { subdomain: orgDetails.subdomain, org_id: user.org_id },
            { headers }
          );
          toast.success(`Subdomain ${orgDetails.subdomain}.sporthouse.ai activated!`);
        } catch (cfError) {
          // If it's already exists error, that's okay
          if (!cfError.response?.data?.detail?.includes('already')) {
            toast.error(cfError.response?.data?.detail || "Failed to create subdomain");
            setSaving(false);
            return;
          }
        }
      }
      
      await axios.put(`${API}/organizations/${user.org_id}`, orgDetails, { headers });
      
      // Clear public page cache so changes are immediately visible
      try {
        await axios.post(`${API}/organizations/${user.org_id}/clear-cache`, {}, { headers });
      } catch (cacheErr) {
        console.warn("Could not clear cache:", cacheErr);
      }
      
      toast.success("Organization details saved");
      trackAdminWorkflowCompleted("settings");
      fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save organization details");
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/organizations/${user.org_id}/stripe-connect`,
        { return_url: window.location.href },
        { headers }
      );
      
      // Redirect to Stripe Connect onboarding
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate Stripe Connect");
      setConnectingStripe(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!confirm("Are you sure you want to disconnect your Stripe account? You will no longer be able to receive payments until you connect a new account.")) {
      return;
    }
    
    setDisconnectingStripe(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(
        `${API}/organizations/${user.org_id}/stripe-connect`,
        { headers }
      );
      
      // Reset Stripe state
      setStripeConnected(false);
      setStripeAccount(null);
      
      toast.success("Stripe account disconnected. You can now connect a new account.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disconnect Stripe account");
    } finally {
      setDisconnectingStripe(false);
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteData.email) {
      toast.error("Please enter an email address");
      return;
    }

    setInviting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/organizations/${user.org_id}/administrators`,
        inviteData,
        { headers }
      );
      toast.success("Administrator invitation sent");
      setInviteDialogOpen(false);
      setInviteData({ email: "", access_level: "operations" });
      fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite administrator");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    if (!confirm("Are you sure you want to remove this administrator?")) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(
        `${API}/organizations/${user.org_id}/administrators/${adminId}`,
        { headers }
      );
      toast.success("Administrator removed");
      fetchOrganization();
    } catch (error) {
      toast.error("Failed to remove administrator");
    }
  };

  const handleResendAdminInvite = async (admin) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/organizations/${user.org_id}/administrators/${admin.admin_id}/resend`,
        {},
        { headers }
      );
      toast.success(`Invitation resent to ${admin.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const handleUpdateAdminAccess = async (admin, accessLevel) => {
    const adminId = admin.admin_id;
    if (!adminId) {
      toast.error("This administrator must accept a new invitation before access can be changed");
      return;
    }
    const operation = accessMutations.begin(adminId);
    if (!operation) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(
        `${API}/organizations/${user.org_id}/administrators/${adminId}/access`,
        { access_level: accessLevel },
        { headers },
      );
      const returned = response?.data;
      if (!returned || !["full", "operations", "finance", "view_only"].includes(returned.access_level) ||
          !Array.isArray(returned.permissions) || !returned.permissions.every((permission) => typeof permission === "string")) {
        accessMutations.hold(operation);
        if (!accessMutations.isCurrent(operation)) return;
        // The write may have committed: do not show stale permissions as current.
        setAdmins((current) => current.map((item) => (
          item.admin_id === adminId ? { ...item, access_level: "unavailable", permissions: undefined } : item
        )));
        toast.error("Access update could not be confirmed. Reload organization settings before making further changes.");
        return;
      }
      if (!accessMutations.isCurrent(operation)) return;
      setAdmins((current) => current.map((item) => (
        item.admin_id === adminId ? { ...item, access_level: returned.access_level, permissions: returned.permissions } : item
      )));
      toast.success("Administrator access updated");
    } catch (error) {
      // Transport/5xx outcomes may follow a committed write. Keep admission closed.
      const knownRejection = [400, 401, 403, 404, 409, 422].includes(error.response?.status);
      if (!knownRejection) accessMutations.hold(operation);
      if (!accessMutations.isCurrent(operation)) return;
      if (!knownRejection) {
        setAdmins((current) => current.map((item) => (
          item.admin_id === adminId ? { ...item, access_level: "unavailable", permissions: undefined } : item
        )));
      }
      toast.error(knownRejection ? "Failed to update administrator access" :
        "Access update outcome is unknown. Do not retry. Reload organization settings to verify stored access before making further changes.");
    } finally {
      accessMutations.finish(operation);
    }
  };

  const handleSaveDocument = async () => {
    if (!docData.name || !docData.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingDoc) {
        await axios.put(
          `${API}/organizations/${user.org_id}/documents/${editingDoc.doc_id}`,
          docData,
          { headers }
        );
        toast.success("Document updated");
      } else {
        await axios.post(
          `${API}/organizations/${user.org_id}/documents`,
          docData,
          { headers }
        );
        toast.success("Document added");
      }
      
      setDocDialogOpen(false);
      setEditingDoc(null);
      setDocData({ name: "", type: "waiver", content: "" });
      fetchOrganization();
    } catch (error) {
      toast.error("Failed to save document");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(
        `${API}/organizations/${user.org_id}/documents/${docId}`,
        { headers }
      );
      toast.success("Document deleted");
      fetchOrganization();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleUseDocumentTemplate = (type) => {
      const templates = {
        waiver: `PARTICIPANT WAIVER AND RELEASE OF LIABILITY

I, the undersigned parent/guardian, hereby acknowledge and agree to the following:

1. ASSUMPTION OF RISK: I understand that participation in ${organization?.name || "this organization"}'s programs involves inherent risks including, but not limited to, physical injury, property damage, and other hazards.

2. RELEASE AND WAIVER: I hereby release, waive, and discharge ${organization?.name || "the organization"}, its officers, directors, employees, coaches, volunteers, and agents from any and all liability, claims, demands, and causes of action.

3. MEDICAL AUTHORIZATION: In the event of an emergency, I authorize ${organization?.name || "the organization"} personnel to secure emergency medical treatment for my child.

4. PHOTO/VIDEO RELEASE: I grant permission for photographs and videos of my child to be used for promotional purposes.

5. RULES AND REGULATIONS: I agree that my child will abide by all rules and regulations of the program.

By signing below, I acknowledge that I have read, understand, and agree to the terms of this waiver.

Parent/Guardian Signature: ___________________ Date: ___________
Printed Name: ___________________`,

        medical: `MEDICAL INFORMATION AND AUTHORIZATION FORM

Player Name: ___________________
Date of Birth: ___________________

EMERGENCY CONTACT INFORMATION:
Primary Contact: ___________________
Phone: ___________________
Relationship: ___________________

MEDICAL INFORMATION:
Primary Care Physician: ___________________
Phone: ___________________

Please list any allergies: ___________________
Please list any medications: ___________________
Please list any medical conditions: ___________________

AUTHORIZATION:
I authorize ${organization?.name || "the organization"} staff to administer basic first aid and, in case of emergency, to transport my child to the nearest medical facility.

Insurance Provider: ___________________
Policy Number: ___________________

Parent/Guardian Signature: ___________________ Date: ___________`,

        code_of_conduct: `CODE OF CONDUCT

${organization?.name || "Our Organization"} is committed to providing a safe, positive, and enjoyable environment for all participants, families, and staff.

PLAYER CODE OF CONDUCT:
• Show respect for coaches, officials, teammates, and opponents
• Play by the rules and demonstrate good sportsmanship
• Be on time for practices and games
• Give maximum effort in all activities
• Use appropriate language at all times
• Take care of equipment and facilities

PARENT/GUARDIAN CODE OF CONDUCT:
• Support your child's participation with positive encouragement
• Respect coaches, officials, and other parents
• Refrain from negative comments or criticism
• Allow coaches to coach during practices and games
• Communicate concerns through appropriate channels

CONSEQUENCES:
Violation of this code may result in:
• Warning
• Suspension from activities
• Removal from the program

By signing below, we agree to uphold this code of conduct.

Player Signature: ___________________ Date: ___________
Parent/Guardian Signature: ___________________ Date: ___________`
      };

      setDocData(prev => ({
        ...prev,
        content: templates[type] || templates.waiver
      }));
      
      toast.info("Template inserted. Have qualified counsel review it before use.");
  };

  const handleSaveProduct = async () => {
    if (!productData.name || !productData.price) {
      toast.error("Please fill in name and price");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingProduct) {
        await axios.put(
          `${API}/organizations/${user.org_id}/products/${editingProduct.product_id}`,
          productData,
          { headers }
        );
        toast.success("Product updated");
      } else {
        await axios.post(
          `${API}/organizations/${user.org_id}/products`,
          productData,
          { headers }
        );
        toast.success("Product added");
      }
      
      setProductDialogOpen(false);
      setEditingProduct(null);
      setProductData({
        name: "", description: "", price: "", photo_url: "",
        has_sizes: false, sizes: [], has_colors: false, colors: []
      });
      fetchOrganization();
    } catch (error) {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(
        `${API}/organizations/${user.org_id}/products/${productId}`,
        { headers }
      );
      toast.success("Product deleted");
      fetchOrganization();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/organizations/${user.org_id}/notifications`,
        notificationSettings,
        { headers }
      );
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadImageToR2 = async (file, imageType, entityId = null) => {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, GIF, or WebP.");
      return null;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return null;
    }

    try {
      // Use direct upload endpoint (goes through backend to cloud storage)
      const formData = new FormData();
      formData.append('file', file);

      const uploadRes = await axios.post(
        `${API}/storage/upload/direct?image_type=${imageType}${entityId ? `&entity_id=${entityId}` : ''}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      return uploadRes.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Upload failed. Please try again.");
      return null;
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, GIF, or WebP.");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    // Open cropper with the selected image
    const previewUrl = URL.createObjectURL(file);
    setLogoCropperImage(previewUrl);
    setLogoCropperOpen(true);
    // Clear the input so the same file can be selected again
    e.target.value = '';
  };

  // Handle cropped logo upload
  const handleCroppedLogoUpload = async (croppedFile) => {
    setLogoCropperOpen(false);
    
    // Show preview immediately
    const previewUrl = URL.createObjectURL(croppedFile);
    setOrgDetails(prev => ({ ...prev, logo_url: previewUrl }));

    // Upload to R2
    const publicUrl = await uploadImageToR2(croppedFile, 'org-logo', orgDetails.org_id);
    if (publicUrl) {
      setOrgDetails(prev => ({ ...prev, logo_url: publicUrl }));
      toast.success("Logo cropped and uploaded!");
    }
    
    // Clean up
    setLogoCropperImage(null);
  };

  const handleSplashUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setOrgDetails(prev => ({ ...prev, splash_image_url: reader.result }));
    };
    reader.readAsDataURL(file);

    // Upload to R2
    const publicUrl = await uploadImageToR2(file, 'org-splash', orgDetails.org_id);
    if (publicUrl) {
      setOrgDetails(prev => ({ ...prev, splash_image_url: publicUrl }));
      toast.success("Splash image uploaded successfully!");
    }
  };

  const handleSidebarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setOrgDetails(prev => ({ ...prev, sidebar_image_url: reader.result }));
    };
    reader.readAsDataURL(file);

    // Upload to R2
    const publicUrl = await uploadImageToR2(file, 'org-sidebar', orgDetails.org_id);
    if (publicUrl) {
      setOrgDetails(prev => ({ ...prev, sidebar_image_url: publicUrl }));
      toast.success("Sidebar image uploaded successfully!");
    }
  };

  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProductData(prev => ({ ...prev, photo_url: reader.result }));
    };
    reader.readAsDataURL(file);

    // Upload to R2
    const publicUrl = await uploadImageToR2(file, 'product', orgDetails.org_id);
    if (publicUrl) {
      setProductData(prev => ({ ...prev, photo_url: publicUrl }));
      toast.success("Product image uploaded!");
    }
  };

  // Subscription handlers
  // Open upgrade dialog instead of redirecting
  const handleUpgradeSubscription = async (tier, interval = 'monthly') => {
    setSelectedUpgradeTier(tier);
    setSelectedUpgradeInterval(interval);
    setUpgradeDialogOpen(true);
  };
  
  // Initialize upgrade - gets client_secret for in-app card entry
  const initializeUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/org/upgrade`,
        { tier: selectedUpgradeTier, interval: selectedUpgradeInterval },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.client_secret) {
        // In-app payment flow
        setUpgradeClientSecret(response.data.client_secret);
        setPendingSubscriptionId(response.data.subscription_id);
      } else if (response.data.success) {
        // Payment completed with saved card
        toast.success(`Successfully upgraded to ${selectedUpgradeTier}!`);
        setUpgradeDialogOpen(false);
        // Refresh subscription data
        const subResponse = await axios.get(`${API}/subscriptions/org/current`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionData(subResponse.data);
      }
    } catch (error) {
      console.error("Error initializing upgrade:", error);
      toast.error(error.response?.data?.detail || "Failed to initialize upgrade");
    } finally {
      setUpgradeLoading(false);
    }
  };
  
  // Confirm upgrade after successful payment
  const confirmUpgrade = async () => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/org/confirm-upgrade`,
        { subscription_id: pendingSubscriptionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Successfully upgraded to ${response.data.tier}!`);
        setUpgradeDialogOpen(false);
        setUpgradeClientSecret(null);
        setPendingSubscriptionId(null);
        
        // Refresh subscription data
        const subResponse = await axios.get(`${API}/subscriptions/org/current`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionData(subResponse.data);
      } else {
        toast.error(response.data.message || "Failed to confirm upgrade");
      }
    } catch (error) {
      console.error("Error confirming upgrade:", error);
      toast.error(error.response?.data?.detail || "Failed to confirm upgrade");
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel your subscription? You'll retain access until the end of your billing period.")) {
      return;
    }
    
    setCancelLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/org/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      
      // Refresh subscription data
      const subResponse = await axios.get(`${API}/subscriptions/org/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionData(subResponse.data);
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setCancelLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/org/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      
      // Refresh subscription data
      const subResponse = await axios.get(`${API}/subscriptions/org/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionData(subResponse.data);
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64" role="status" aria-label="Loading organization settings">
          <span className="sr-only">Loading organization settings</span>
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </PageLayout>
    );
  }

  if (!organization) {
    return (
      <PageLayout title="Organization Settings">
        <div className="text-center py-16">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          {activeTab === "banking" && user?.org_id ? (
            <div role="alert">Organization unavailable. Reload settings to try again.</div>
          ) : <>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Organization Found</h2>
            <p className="text-slate-500">You are not associated with any organization.</p>
          </>}
        </div>
      </PageLayout>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "setup":
        return (
          <SetupOverviewTab
            organization={organization}
            orgDetails={orgDetails}
            stripeConnected={stripeConnected}
            admins={admins}
            documents={documents}
            onOpenSection={navigateToTab}
          />
        );

      case "details":
        return (
          <DetailsTab
            orgDetails={orgDetails}
            setOrgDetails={setOrgDetails}
            handleLogoUpload={handleLogoUpload}
            handleSplashUpload={handleSplashUpload}
            handleSidebarUpload={handleSidebarUpload}
            handleSaveDetails={handleSaveDetails}
            saving={saving}
            subdomainChecking={subdomainChecking}
            subdomainAvailable={subdomainAvailable}
            subdomainError={subdomainError}
          />
        );

      case "subscription":
        return (
          <SubscriptionTab
            subscriptionData={subscriptionData}
            subscriptionLoading={subscriptionLoading}
            handleUpgradeSubscription={handleUpgradeSubscription}
            handleCancelSubscription={handleCancelSubscription}
            handleReactivateSubscription={handleReactivateSubscription}
            upgradeLoading={upgradeLoading}
            cancelLoading={cancelLoading}
          />
        );

      case "billing":
        return (
          <BillingTab
            token={token}
            API={API}
          />
        );

      case "banking":
        return (
          <BankingTab
            token={token}
            API={API}
            user={user}
            organization={organization}
            setOrganization={setOrganization}
            stripeConnected={stripeConnected}
            stripeAccount={stripeAccount}
            stripeStatusLoading={stripeStatus.loading}
            stripeStatusError={stripeStatus.error}
            handleRetryStripeStatus={() => setStripeStatusRetry(value => value + 1)}
            connectingStripe={connectingStripe}
            disconnectingStripe={disconnectingStripe}
            handleConnectStripe={handleConnectStripe}
            handleDisconnectStripe={handleDisconnectStripe}
          />
        );

      case "fees":
        return (
          <FeesTab setActiveTab={navigateToTab} />
        );

      case "foundation":
        return (
          <FoundationTab />
        );

      case "administrators":
        return (
          <AdministratorsTab
            ownerUserId={organization?.owner_user_id}
            adminUserId={organization?.admin_user_id}
            user={user}
            admins={admins}
            setInviteDialogOpen={openInviteDialog}
            handleResendAdminInvite={handleResendAdminInvite}
            handleRemoveAdmin={handleRemoveAdmin}
            handleUpdateAdminAccess={handleUpdateAdminAccess}
            isAccessUpdateBlocked={accessMutations.isBlocked}
            isAccessUpdateUnknown={accessMutations.isUnknown}
            needsAccessVerification={accessMutations.needsVerification}
            handleRefreshAdminAccess={async (adminId) => {
              const verified = await accessMutations.refresh(adminId, async () => {
                const response = await axios.get(`${API}/organizations/${user.org_id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const org = response?.data;
                if (!org || org.org_id !== user.org_id || !Array.isArray(org.administrators)) throw new Error("Unverified organization");
                const matches = org.administrators.filter((item) => item && item.admin_id === adminId);
                const member = matches[0];
                if (matches.length !== 1 || !["full", "operations", "finance", "view_only"].includes(member.access_level) ||
                    !Array.isArray(member.permissions) || !member.permissions.every((permission) => typeof permission === "string")) throw new Error("Unverified access");
                return org;
              }, (org) => {
                setOrganization(org);
                setAdmins(org.administrators);
              });
              // No retry PUT: an unsuccessful read leaves the verification gate closed.
              if (!verified) return;
            }}
          />
        );

      case "documents":
        return (
          <DocumentsTab
            documents={documents}
            setEditingDoc={setEditingDoc}
            setDocData={setDocData}
            setDocDialogOpen={openDocumentDialog}
            handleDeleteDocument={handleDeleteDocument}
          />
        );

      case "products":
        return (
          <ProductsTab
            products={products}
            setEditingProduct={setEditingProduct}
            setProductData={setProductData}
            setProductDialogOpen={openProductDialog}
            handleDeleteProduct={handleDeleteProduct}
          />
        );

      case "compliance":
        return (
          <ComplianceSettings />
        );

      case "merchandise":
        return (
          <OrgMerchandise organization={organization} />
        );

      case "integrations":
        return (
          <IntegrationsTab
            organization={organization}
            stripeConnected={stripeConnected}
          />
        );

      case "notifications":
        return (
          <NotificationsTab
            notificationSettings={notificationSettings}
            setNotificationSettings={setNotificationSettings}
            handleSaveNotifications={handleSaveNotifications}
            saving={saving}
          />
        );

      case "import":
        return (
          <ImportTab
            token={token}
            API={API}
            importHistory={importHistory}
            loadingImportHistory={loadingImportHistory}
            setSelectedImportType={setSelectedImportType}
            setImportWizardOpen={setImportWizardOpen}
          />
        );

      case "developer":
        return (
          <section aria-labelledby="developer-settings-heading" className="space-y-4">
            <h2 id="developer-settings-heading" className="text-lg font-semibold text-slate-900">Developer</h2>
            <p className="text-sm text-slate-600">Manage developer applications, OAuth access, and webhooks for your organization.</p>
            <Button asChild>
              <a href={developerPath}><Code2 className="mr-2 h-4 w-4" />Open developer console</a>
            </Button>
          </section>
        );

      default:
        return null;
    }
  };

  // Filter tabs based on user role - Foundation is only for super_admin
  const visibleTabs = TABS.filter(tab => {
    if (tab.id === "foundation" && user?.role !== "super_admin") {
      return false;
    }
    return true;
  });
  const filteredTabs = visibleTabs.filter((tab) =>
    tab.label.toLowerCase().includes(settingsSearch.trim().toLowerCase())
  );
  const tabGroups = [...new Set(filteredTabs.map((tab) => tab.group))];
  return (
    <PageLayout
      title="Organization Settings"
      subtitle="Manage your organization profile, payments, people, integrations, and platform preferences."
      headerEyebrow="Administration"
    >
      <div className="settings-control-center settings-compact">
        {embedded && <SettingsHeader />}
        <div className="settings-mobile-navigation mb-4 lg:hidden">
        <Label htmlFor="settings-section" className="mb-2 block text-sm font-medium text-slate-700">Settings section</Label>
        <Select value={activeTab} onValueChange={navigateToTab} onOpenChange={() => { sectionTypeahead.current = { text: "", at: 0 }; }}>
          <SelectTrigger id="settings-section" className="h-12 w-full rounded-xl bg-white" data-testid="settings-mobile-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent onKeyDownCapture={handleSectionTypeahead}>
            {visibleTabs.map((tab) => <SelectItem key={tab.id} value={tab.id}>{tab.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-8 organization-settings-workspace" data-testid="org-settings-page">
        {/* Sidebar Tabs */}
        <div className="settings-navigation-column hidden flex-shrink-0 lg:block">
          <nav className="settings-navigation sticky top-8" aria-label="Organization settings">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={settingsSearch} onChange={(event) => setSettingsSearch(event.target.value)} placeholder="Find a setting" className="h-10 rounded-xl border-slate-200 pl-9" aria-label="Find a setting" />
            </div>
            {tabGroups.map((group) => (
              <div key={group} className="mb-3 last:mb-0">
                {group !== "Overview" && <p className="settings-navigation-group px-3 pb-1 pt-2">{group}</p>}
                {filteredTabs.filter((tab) => tab.group === group).map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => navigateToTab(tab.id)}
                      className={`settings-navigation-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${isActive ? 'is-active' : ''}`}
                      data-testid={`tab-${tab.id}`}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
            {filteredTabs.length === 0 && <p className="px-3 py-6 text-center text-sm text-slate-500">No settings found.</p>}
          </nav>
        </div>

        {/* Main Content */}
        <div ref={settingsContentRef} tabIndex={-1} className="settings-section-content flex-1 min-w-0">
          {renderTabContent()}
        </div>
      </div>
      </div>

      {/* Invite Admin Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sh-administrators-invite-dialog" onCloseAutoFocus={(event) => {
          event.preventDefault();
          const opener = inviteOpenerRef.current;
          if (opener?.isConnected && !opener.disabled && !opener.closest('[hidden], [inert]')) {
            opener.focus();
          }
          if (document.activeElement !== opener) settingsContentRef.current?.focus();
        }}>
          <DialogHeader>
            <DialogTitle>Invite Administrator</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization as an administrator.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="invite-admin-email" className="text-slate-900">Email Address</Label>
              <Input id="invite-admin-email"
                type="email"
                className="mt-1 border-slate-300"
                placeholder="admin@example.com"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="invite-admin-access" className="text-slate-900 mb-2 block">Access level</Label>
              <Select value={inviteData.access_level} onValueChange={(value) => setInviteData((current) => ({ ...current, access_level: value }))}>
                <SelectTrigger id="invite-admin-access" className="h-12 rounded-xl bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_ACCESS_LEVELS.map((level) => (
                    <SelectItem key={level.id} value={level.id}>{level.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-sm text-slate-500">
                {ADMIN_ACCESS_LEVELS.find((level) => level.id === inviteData.access_level)?.description}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)} className="border-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleInviteAdmin}
              disabled={inviting || !inviteData.email}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Dialog */}
      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent ref={setOperationalDialogElement} className="sh-documents-products-dialog max-w-2xl" onCloseAutoFocus={(event) => restoreOperationalOpener(event, documentOpenerRef)}>
          <DialogHeader>
            <DialogTitle>{editingDoc ? "Edit Document" : "Add Document"}</DialogTitle>
            <DialogDescription>
              Create a document that can be used across your programs.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="settings-document-name" className="text-slate-900">Document Name</Label>
                <Input id="settings-document-name"
                  className="mt-1 border-slate-300"
                  placeholder="Liability Waiver"
                  value={docData.name}
                  onChange={(e) => setDocData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="settings-document-type" className="text-slate-900">Document Type</Label>
                <Select
                  value={docData.type}
                  onValueChange={(v) => setDocData(prev => ({ ...prev, type: v }))}
                >
                  <SelectTrigger id="settings-document-type" className="mt-1 border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="waiver">Waiver</SelectItem>
                    <SelectItem value="medical">Medical Form</SelectItem>
                    <SelectItem value="code_of_conduct">Code of Conduct</SelectItem>
                    <SelectItem value="policy">Policy</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="settings-document-content" className="text-slate-900">Content</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUseDocumentTemplate(docData.type)}
                  className="border-slate-300"
                >
                  Use starter template
                </Button>
              </div>
              <Textarea id="settings-document-content"
                className="mt-1 border-slate-300 font-mono text-sm"
                rows={12}
                placeholder="Enter document content..."
                value={docData.content}
                onChange={(e) => setDocData(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDialogOpen(false)} className="border-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleSaveDocument}
              disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {saving ? "Saving..." : editingDoc ? "Update Document" : "Add Document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent ref={setOperationalDialogElement} className="sh-documents-products-dialog" onCloseAutoFocus={(event) => restoreOperationalOpener(event, productOpenerRef)}>
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription>
              Create a product that can be added to your programs as an add-on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Product Image */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center overflow-hidden">
                {productData.photo_url ? (
                  <img src={productData.photo_url} alt="Product" className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <button type="button" onClick={() => productImageInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-full cursor-pointer hover:bg-slate-50">
                <Upload className="w-4 h-4" />
                <span className="text-sm">Upload Image</span>
              </button>
              <input ref={productImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleProductImageUpload} />
            </div>

            <div>
              <Label htmlFor="settings-product-name" className="text-slate-900">Product Name *</Label>
              <Input id="settings-product-name" aria-required="true"
                className="mt-1 border-slate-300"
                placeholder="Team Jersey"
                value={productData.name}
                onChange={(e) => setProductData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="settings-product-price" className="text-slate-900">Price *</Label>
              <Input id="settings-product-price" aria-required="true"
                type="number"
                className="mt-1 border-slate-300"
                placeholder="25.00"
                value={productData.price}
                onChange={(e) => setProductData(prev => ({ ...prev, price: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="settings-product-description" className="text-slate-900">Description</Label>
              <Textarea id="settings-product-description"
                className="mt-1 border-slate-300"
                rows={2}
                placeholder="Optional description..."
                value={productData.description}
                onChange={(e) => setProductData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={productData.has_sizes}
                  onCheckedChange={(checked) => setProductData(prev => ({ ...prev, has_sizes: checked }))}
                />
                <span className="text-sm text-slate-900">Has Sizes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={productData.has_colors}
                  onCheckedChange={(checked) => setProductData(prev => ({ ...prev, has_colors: checked }))}
                />
                <span className="text-sm text-slate-900">Has Colors</span>
              </label>
            </div>

            {productData.has_sizes && (
              <div>
                <Label htmlFor="settings-product-sizes" className="text-slate-900 text-sm">Sizes (comma separated)</Label>
                <Input id="settings-product-sizes"
                  className="mt-1 border-slate-300"
                  placeholder="S, M, L, XL"
                  value={productData.sizes.join(", ")}
                  onChange={(e) => setProductData(prev => ({
                    ...prev,
                    sizes: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                  }))}
                />
              </div>
            )}

            {productData.has_colors && (
              <div>
                <Label htmlFor="settings-product-colors" className="text-slate-900 text-sm">Colors (comma separated)</Label>
                <Input id="settings-product-colors"
                  className="mt-1 border-slate-300"
                  placeholder="Red, Blue, Black"
                  value={productData.colors.join(", ")}
                  onChange={(e) => setProductData(prev => ({
                    ...prev,
                    colors: e.target.value.split(",").map(c => c.trim()).filter(Boolean)
                  }))}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)} className="border-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleSaveProduct}
              disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {saving ? "Saving..." : editingProduct ? "Update Product" : "Add Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Wizard */}
      <ImportWizard 
        open={importWizardOpen} 
        onOpenChange={(open) => {
          setImportWizardOpen(open);
          if (!open) setSelectedImportType(null);
        }}
        initialImportType={selectedImportType}
        onImportComplete={async () => {
          // Refresh import history
          try {
            const response = await axios.get(`${API}/import/history`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setImportHistory(response.data || []);
          } catch (error) {
            console.error("Error fetching import history:", error);
          }
          toast.success("Import completed successfully");
        }}
      />
      
      {/* Subscription Upgrade Dialog */}
      <Dialog 
        open={upgradeDialogOpen} 
        onOpenChange={(open) => {
          setUpgradeDialogOpen(open);
          if (!open) {
            setUpgradeClientSecret(null);
            setPendingSubscriptionId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(() => {
                const tierOrder = { free: 0, plus: 1, premium: 2 };
                const currentTierLevel = tierOrder[subscriptionData?.tier] || 0;
                const targetTierLevel = tierOrder[selectedUpgradeTier] || 0;
                const isDowngrade = targetTierLevel < currentTierLevel;
                return (
                  <>
                    <Crown className={`w-5 h-5 ${isDowngrade ? 'text-slate-500' : 'text-amber-500'}`} />
                    {isDowngrade ? 'Downgrade' : 'Upgrade'} to {selectedUpgradeTier?.charAt(0).toUpperCase() + selectedUpgradeTier?.slice(1)}
                  </>
                );
              })()}
            </DialogTitle>
            <DialogDescription>
              {selectedUpgradeInterval === 'monthly' 
                ? `$${selectedUpgradeTier === 'plus' ? '49' : '149'}/month`
                : `$${selectedUpgradeTier === 'plus' ? '470' : '1,430'}/year (save ~20%)`}
            </DialogDescription>
          </DialogHeader>
          
          {!upgradeClientSecret ? (
            <div className="py-4 space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={selectedUpgradeInterval === 'monthly' ? 'default' : 'outline'}
                  onClick={() => setSelectedUpgradeInterval('monthly')}
                  className="flex-1"
                >
                  Monthly
                </Button>
                <Button
                  variant={selectedUpgradeInterval === 'annual' ? 'default' : 'outline'}
                  onClick={() => setSelectedUpgradeInterval('annual')}
                  className="flex-1"
                >
                  Annual (Save 20%)
                </Button>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900 mb-2">
                  {selectedUpgradeTier === 'plus' ? 'Plus' : 'Premium'} includes:
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  {selectedUpgradeTier === 'plus' ? (
                    <>
                      <li>✓ Up to 25 programs</li>
                      <li>✓ Unlimited registrations</li>
                      <li>✓ Advanced reporting</li>
                      <li>✓ 4% transaction fee</li>
                    </>
                  ) : (
                    <>
                      <li>✓ Unlimited programs</li>
                      <li>✓ Full analytics suite</li>
                      <li>✓ Tournament management</li>
                      <li>✓ AI-powered features</li>
                      <li>✓ 3% transaction fee</li>
                    </>
                  )}
                </ul>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={initializeUpgrade}
                  disabled={upgradeLoading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {upgradeLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Continue to Payment
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret: upgradeClientSecret }}>
              <SubscriptionPaymentForm 
                clientSecret={upgradeClientSecret}
                onSuccess={confirmUpgrade}
                onCancel={() => {
                  setUpgradeDialogOpen(false);
                  setUpgradeClientSecret(null);
                  setPendingSubscriptionId(null);
                }}
                tier={selectedUpgradeTier}
                interval={selectedUpgradeInterval}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>

      {/* Logo Image Cropper Modal */}
      {logoCropperOpen && logoCropperImage && (
        <ImageCropper
          imageSrc={logoCropperImage}
          aspectRatio={1}
          transparentBackground={true}
          title="Crop Organization Logo"
          onCropComplete={handleCroppedLogoUpload}
          onCancel={() => {
            setLogoCropperOpen(false);
            setLogoCropperImage(null);
          }}
        />
      )}
    </PageLayout>
  );
};

// Subscription Payment Form Component using Stripe Elements
const SubscriptionPaymentForm = ({ clientSecret, onSuccess, onCancel, tier, interval }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      // Use confirmPayment for new Stripe API with confirmation_secret
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });
      
      if (stripeError) {
        // If error type is card_error or validation_error, show to customer
        if (stripeError.type === "card_error" || stripeError.type === "validation_error") {
          setError(stripeError.message);
        } else {
          setError("An unexpected error occurred.");
        }
        setProcessing(false);
        return;
      }
      
      // Payment succeeded
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="py-4 space-y-4">
      <div className="p-4 bg-slate-50 rounded-lg">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Payment Details
        </label>
        <PaymentElement 
          options={{
            layout: 'tabs'
          }}
        />
      </div>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      
      <div className="flex justify-between items-center pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={!stripe || processing}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          {processing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Lock className="w-4 h-4 mr-2" />
          )}
          {processing ? 'Processing...' : `Pay $${interval === 'monthly' 
            ? (tier === 'plus' ? '49' : '149') 
            : (tier === 'plus' ? '470' : '1,430')}`}
        </Button>
      </div>
      
      <p className="text-xs text-center text-slate-500">
        🔒 Secured by Stripe. Your payment info is encrypted.
      </p>
    </form>
  );
};

export default AdminOrganizationSettings;
